// src/components/onboarding/OnboardingPanel.jsx
//
// Steps B–E of "Post Won Proposal Steps" — everything after the client's
// contract (Step A, which lives in ContractsPanel beside the Send / Record
// controls). Appears ONCE PER PROPOSAL, once a contract is signed (or Step A
// is marked nothing-to-do). Nothing fires automatically — Craig runs each step.
//
// It is one panel per proposal and not one per signed envelope because the
// onboarding record belongs to the proposal; see onboardingRecord.js for what
// went wrong when it did not.
//
// ⛔ THE LETTERS ON SCREEN ARE NOT THE LETTERS ON THE WIRE. postWonSteps.js is
// the one display map: shown B = backend "A" (the Autotask contract), shown
// C = backend "B" (tickets), shown D = backend "C" (onboarding ticket +
// checklist), shown E = quotes (local state, no backend). `/run` still takes
// the backend letters and record.errors still stores them; this file remaps
// on READ (stepForBackendCode) and never rewrites a stored letter.
//
// ── STEP ORDER IS THE SAFETY MODEL ─────────────────────────────────────────
// B→C→D is the order the backend enforces (the contract must exist before
// tickets reference the work it pays for); each is behind its own review
// modal, and B — the one financial record — shows the plan's blockers and is
// disabled when Stage A cannot run, so Craig is never pressed into a 409.
// E (quotes) runs here through saveSnapshot and never touches the backend.
//
// "Nothing to do": a per-step flag keyed by STEP ID on the record
// (postWonSteps.toggleSkippedPatch). Skipped is not done and renders
// differently; it is reversible in one click; and it never blocks a real run —
// running the step clears its flag.
import React, { useState, useEffect, useCallback } from "react";
import { missingOnboardingQuotes, existingOnboardingQuotes } from "./onboardingQuotes";
import { onboardingRecordOf, signedAtFor, hasSplitLegacyRecords, onboardingPatch } from "./onboardingRecord";
import { POST_WON_STEPS, stepById, stepTitle, stepState, toggleSkippedPatch, clearSkippedForStages, unmappedErrors } from "./postWonSteps";
import OnboardingPlanModal from "./OnboardingPlanModal";
import OnboardingTicketsModal from "./OnboardingTicketsModal";
import { useTicketPicklists } from "../quotes/useTicketPicklists";
import { StepCard } from "./StepCard";

const API_BASE = window.location.hostname === "localhost"
  ? "http://localhost:3001"
  : "https://msp-os-api-ggd7bye7gcb4gqdb.australiasoutheast-01.azurewebsites.net";

const post = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${window.__mspToken || ""}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(data.error || res.statusText), { data });
  return data;
};

const btn = (primary, disabled) => ({
  appearance: "none", border: 0, borderRadius: 6, padding: "7px 13px", fontSize: 11,
  fontWeight: 600, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1,
  background: primary ? "#0b80a5" : "var(--s2,#eef2f7)", color: primary ? "#fff" : "var(--text,#333)",
});

const STATUS_STYLE = {
  not_started: { bg: "#eef2f7", fg: "#475569", label: "Not started" },
  in_progress: { bg: "#e0f2fe", fg: "#0369a1", label: "In progress" },
  partial:     { bg: "#fef3c7", fg: "#b45309", label: "Partial" },
  complete:    { bg: "#dcfce7", fg: "#15803d", label: "Complete" },
};

export default function OnboardingPanel({
  /* ⚠ NO `instance` PROP, deliberately. The record is the PROPOSAL's — see
     onboardingRecord.js. This panel used to be rendered once per SIGNED
     ContractInstance and to store its result there, so a proposal with two
     signed envelopes ran the whole pipeline twice and created two Autotask
     contracts. ContractsPanel now renders exactly one of these per proposal. */
  proposal, contractInstances = [], patchProposal,
  saveSnapshot, quotes = [], setQuotes, flash,
}) {
  const [companies, setCompanies] = useState(null);
  const [companyQuery, setCompanyQuery] = useState("");
  /* ⚠ THE ORDER OF THESE TWO FALLBACKS MATTERS.
     A completed run's own record wins, because it records what was ACTUALLY
     written to Autotask. `proposal.companyId` is the pre-fill for a run that
     has not happened yet — a stored link someone picked, never a match
     inferred here. ⛔ Still a PRE-FILL, never an auto-confirm: Stage B's
     review modal is the human confirmation. */
  const [companyId, setCompanyId] = useState(
    () => onboardingRecordOf(proposal, contractInstances)?.companyID
          || proposal.companyId
          || ""
  );
  /* The review modals — { kind: "A" | "B" | "C", plan, reviewOnly, result }.
     kind is the BACKEND stage the modal runs (postWonSteps maps it to the
     shown letter). The plan is the STRUCTURED half of /plan; the modal shows
     it and posts back the narrow override allowlist (onboardingReview.js).
     The picklists for the tickets modal are fetched ONCE per open, here. */
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(null);
  /* The plan, fetched once a company is chosen, so Step B can show the
     blockers and be disabled rather than pressed into a 409, and Step C can
     show n/m. /plan writes nothing. */
  const [plan, setPlan] = useState(null);
  const { picklists, error: picklistsError } = useTicketPicklists(modal && modal.kind !== "A" ? modal.plan?.proposalId : null);

  const rec = onboardingRecordOf(proposal, contractInstances);
  const split = hasSplitLegacyRecords(proposal, contractInstances);
  const status = rec?.status || "not_started";
  const st = STATUS_STYLE[status] || STATUS_STYLE.not_started;

  useEffect(() => {
    if (companies) return;
    fetch(`${API_BASE}/api/autotask-data/companies`, {
      headers: { Authorization: `Bearer ${window.__mspToken || ""}` },
    })
      .then((r) => r.json())
      .then((d) => setCompanies(Array.isArray(d) ? d : (d.data || d.companies || [])))
      .catch(() => setCompanies([]));
  }, [companies]);

  /* Writes to the PROPOSAL. Two signed envelopes therefore converge on one
     record, and Stage B's `if (!record.autotaskContractId)` guard sees the
     contract the other envelope already created. */
  const patchRecord = useCallback((patch) => { patchProposal(patch); }, [patchProposal]);

  const chosen = (companies || []).find((c) => String(c.id) === String(companyId)) || null;
  const matches = (companies || [])
    .filter((c) => c.companyName?.toLowerCase().includes(companyQuery.toLowerCase()))
    .slice(0, 8);

  /* The EARLIEST signature across every signed envelope — the pipeline runs
     on the first one back, and this becomes the Autotask contract start
     date, so a later envelope must not redate an existing contract. */
  const signedAt = signedAtFor(contractInstances, proposal?.id);

  const planBody = { proposal, companyID: companyId || null, companyName: chosen?.companyName || null, signedAt };

  useEffect(() => {
    let alive = true;
    post("/api/onboarding/plan", planBody)
      .then((out) => { if (alive) setPlan(out.plan); })
      .catch(() => { if (alive) setPlan(null); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal?.id, proposal?.updatedAt, companyId, signedAt]);

  const openModal = async (kind, reviewOnly = false) => {
    setBusy("preview");
    try {
      const out = await post("/api/onboarding/plan", planBody);
      setPlan(out.plan);
      setModal({ kind, plan: out.plan, reviewOnly, result: null });
    } catch (e) {
      flash?.(`Preview failed: ${e.message}`);
    } finally { setBusy(null); }
  };

  // ── Step E — quotes, local state, never the backend ───────────────────────
  const runQuotes = async () => {
    const missing = await missingOnboardingQuotes(proposal, quotes);
    if (!missing.length) {
      flash?.("Every foundational block already has a quote — nothing to add.");
      return;
    }
    setQuotes((prev) => {
      const next = [...missing, ...prev];
      saveSnapshot({ quotes: next });
      return next;
    });
    const base = { ...(rec || {}), quoteIds: [...((rec || {}).quoteIds || []), ...missing.map((q) => q.id)] };
    /* A real run clears the step's nothing-to-do flag. */
    const skipped = base.skipped ? { ...base.skipped, quotes: null } : base.skipped;
    patchRecord({ onboarding: skipped ? { ...base, skipped } : base });
    flash?.(`${missing.length} quote${missing.length === 1 ? "" : "s"} created.`);
  };

  // ── Steps B/C/D — backend, Autotask. `stages` are BACKEND letters. ────────
  const runStages = async (stages, label, extra = {}) => {
    setBusy(label);
    try {
      const out = await post("/api/onboarding/run", { ...planBody, stages, onboarding: rec || undefined, ...extra });
      // Stored back HERE — the backend deliberately never writes to the state
      // blob, so a result that is not persisted by this call is lost, and a
      // contract created a second ago becomes an orphan nobody knows about.
      // The run also clears the nothing-to-do flags for the stages it covered.
      patchRecord(onboardingPatch(clearSkippedForStages(out.onboarding, stages), companyId));
      const errs = (out.onboarding.errors || []).length;
      flash?.(errs ? `${label} finished with ${errs} problem${errs === 1 ? "" : "s"}.` : `${label} complete.`);
      return out;
    } catch (e) {
      const blockers = e.data?.blockers;
      flash?.(blockers ? `Blocked: ${blockers.join(" ")}` : `${label} failed: ${e.message}`);
      if (e.data?.onboarding) patchRecord({ onboarding: e.data.onboarding });
      return null;
    } finally { setBusy(null); }
  };

  const labelFor = (code) => stepTitle(POST_WON_STEPS.find((s) => s.backendCode === code));

  const confirmContract = async ({ overrides, confirmBaseline }) => {
    if (!chosen) { flash?.("Pick the Autotask company first."); return; }
    const out = await runStages(["A"], labelFor("A"), { overrides, confirmBaseline });
    if (out) setModal(null);
  };
  const confirmTickets = (code) => async ({ overrides }) => {
    const out = await runStages([code], labelFor(code), { overrides });
    if (out) setModal((m) => (m ? { ...m, result: out.onboarding } : m));
  };

  const toggleSkip = (id) => patchRecord(toggleSkippedPatch(rec, id));

  const madeQuotes = existingOnboardingQuotes(quotes, proposal?.id).length;
  const blockCount = (proposal?.content?.foundationalBlocks || []).length;
  const plannedTickets = plan ? plan.stageB.tickets.length : null;
  const ctx = { proposal, contractInstances, record: rec, plannedTickets, quoteCount: madeQuotes };
  const stateOf = (id) => stepState(id, ctx);
  const blockers = plan ? plan.blockers : (companyId ? [] : ["No Autotask company chosen."]);
  const canRunContract = !!companyId && (!plan || plan.canRunStageA);
  const unlockedForTickets = !!companyId;
  const stray = unmappedErrors(rec);

  const B = stepById("at_contract"); const C = stepById("tickets"); const D = stepById("onboarding_tkt"); const E = stepById("quotes");

  return (
    <div data-testid="onboarding-steps" style={{ marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                       background: st.bg, color: st.fg, textTransform: "uppercase", letterSpacing: ".06em" }}>
          {st.label}
        </span>
        <span style={{ fontSize: 11, color: "var(--text3,#777)" }}>Steps B–D write to Autotask in that order; E is local.</span>
        <span style={{ marginLeft: "auto" }}>
          <button style={btn(false, busy === "preview")} disabled={!!busy} onClick={() => openModal("A", true)}
                  title="The full plan for B, C and D — the contract, every line, every ticket, the warnings. Writes nothing.">
            {busy === "preview" ? "…" : "🔍 Preview (writes nothing)"}
          </button>
        </span>
      </div>

      {split && (
        <div style={{ fontSize: 11, lineHeight: 1.5, padding: "7px 9px", marginBottom: 8, borderRadius: 6, background: "#fef3c7", color: "#92400e" }}>
          <strong>Two onboarding records found on this proposal.</strong> Each signed contract carries its own, which means the
          pipeline ran twice and there are probably two Autotask contracts. Check Autotask and deactivate the one that is
          wrong before running any step here.
        </div>
      )}

      {/* ── The company, which nothing can derive ───────────────────────── */}
      <div style={{ fontSize: 11, color: "var(--text2,#555)", marginBottom: 4 }}>
        Autotask company <span style={{ color: "var(--text3,#999)" }}>— required for Stages B, C and D</span>
      </div>
      {chosen ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{chosen.companyName}</div>
          <span style={{ fontSize: 10, color: "var(--text3,#999)" }}>#{chosen.id}</span>
          <button style={btn(false)} onClick={() => { setCompanyId(""); setCompanyQuery(""); }}>Change</button>
        </div>
      ) : (
        <>
          <input value={companyQuery} onChange={(e) => setCompanyQuery(e.target.value)}
                 placeholder={companies ? "Search Autotask companies…" : "Loading companies…"}
                 style={{ width: "100%", fontSize: 12, padding: "7px 9px", borderRadius: 6, border: "1px solid var(--border,#d0d8e0)", marginBottom: 4 }} />
          {/* A picker, never an auto-match by NAME — name-matching a client is
              what previously put one client's report on another's record. */}
          {companyQuery.length >= 2 && matches.map((c) => (
            <div key={c.id} onClick={() => { setCompanyId(c.id); setCompanyQuery(""); }}
                 style={{ fontSize: 12, padding: "5px 8px", cursor: "pointer", borderRadius: 5, display: "flex", gap: 8 }}>
              <span style={{ flex: 1 }}>{c.companyName}</span>
              <span style={{ color: "var(--text3,#999)", fontSize: 10 }}>#{c.id}</span>
            </div>
          ))}
          {companyQuery.length >= 2 && !matches.length && (
            <div style={{ fontSize: 11, color: "var(--text3,#999)", padding: "4px 8px" }}>No match.</div>
          )}
        </>
      )}

      {/* ── Stage B — Contracts for Autotask (backend "A") ───────────────── */}
      <StepCard step={B} state={stateOf("at_contract")} busy={busy} onToggleSkip={() => toggleSkip("at_contract")}
        note={blockers.length > 0 && !rec?.autotaskContractId ? (
          <div data-testid="step-at_contract-blockers" style={{ fontSize: 10.5, background: "#fee2e2", border: "1px solid #fca5a5", color: "#991b1b", borderRadius: 6, padding: "6px 9px", marginTop: 6, lineHeight: 1.5 }}>
            <strong>Cannot run yet.</strong> {blockers.join(" ")}
          </div>
        ) : null}
        actions={
          <button style={btn(true, !!busy || !canRunContract)} disabled={!!busy || !canRunContract} onClick={() => openModal("A")}
                  title={!companyId ? "Pick the Autotask company first" : plan && !plan.canRunStageA ? "Stage B cannot run — see the blockers" : "Review the contract and its lines before anything is written"}>
            {busy === labelFor("A") ? "…" : rec?.autotaskContractId ? "Review / re-run" : "⚠ Review & create contract"}
          </button>
        } />

      {/* ── Stage C — Tickets (backend "B") ──────────────────────────────── */}
      <StepCard step={C} state={stateOf("tickets")} busy={busy} onToggleSkip={() => toggleSkip("tickets")}
        actions={
          <button style={btn(false, !!busy || !unlockedForTickets)} disabled={!!busy || !unlockedForTickets} onClick={() => openModal("B")}
                  title={unlockedForTickets ? "Review each ticket before it is created" : "Pick the Autotask company first"}>
            {busy === labelFor("B") ? "…" : `Review & create tickets${plannedTickets != null ? ` (${(rec?.ticketIds || []).length}/${plannedTickets})` : blockCount ? ` (${(rec?.ticketIds || []).length}/${blockCount}+)` : ""}`}
          </button>
        } />

      {/* ── Stage D — Onboarding ticket & checklist (backend "C") ─────────── */}
      <StepCard step={D} state={stateOf("onboarding_tkt")} busy={busy} onToggleSkip={() => toggleSkip("onboarding_tkt")}
        actions={
          <button style={btn(false, !!busy || !unlockedForTickets)} disabled={!!busy || !unlockedForTickets} onClick={() => openModal("C")}
                  title={unlockedForTickets ? "Review the onboarding ticket before it is created" : "Pick the Autotask company first"}>
            {busy === labelFor("C") ? "…" : rec?.onboardingTicketId ? "Review / re-run" : "Review & create onboarding ticket"}
          </button>
        } />

      {/* ── Stage E — Quotes (local) ─────────────────────────────────────── */}
      <StepCard step={E} state={stateOf("quotes")} busy={busy} onToggleSkip={() => toggleSkip("quotes")}
        actions={
          <button style={btn(false, !!busy || !blockCount)} disabled={!!busy || !blockCount} onClick={runQuotes}
                  title={blockCount ? "" : "This proposal has no foundational blocks"}>
            Create quotes ({madeQuotes}/{blockCount})
          </button>
        } />

      {stray.length > 0 && (
        <div data-testid="unmapped-errors" style={{ marginTop: 7 }}>
          {stray.map((e, i) => (
            <div key={i} style={{ fontSize: 10.5, color: "#c0392b", marginBottom: 2 }}>Stage {e?.stage}: {e?.message}</div>
          ))}
        </div>
      )}

      {modal?.kind === "A" && (
        <OnboardingPlanModal plan={modal.plan} busy={busy === labelFor("A")} reviewOnly={modal.reviewOnly}
                             onCancel={() => setModal(null)} onConfirm={confirmContract} />
      )}
      {(modal?.kind === "B" || modal?.kind === "C") && (
        <OnboardingTicketsModal plan={modal.plan} record={rec} stages={[modal.kind]} picklists={picklists} picklistsError={picklistsError}
                                busy={busy === labelFor(modal.kind)} result={modal.result}
                                onCancel={() => setModal(null)} onConfirm={confirmTickets(modal.kind)} />
      )}
    </div>
  );
}
